// Copyright ©️ 2026 eVolve MEP, LLC

import './image-upload.css';

import React, { useEffect, useState } from 'react';

import type { StrongOmit, FileId } from '@evolvemep/api-ts';
import {
  FileButton,
  type FileButtonProps,
  Avatar,
  LoadingOverlay,
  ActionIcon,
  Flex,
  Text,
  Overlay,
  Stack,
  InputLabel,
} from '@mantine/core';
import { useUncontrolled } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { AgGridStyleTooltip } from 'components/Mantine/AgGridStyleTooltip';
import { usePresignedFile } from 'components/Mantine/FileUploads/ImageFromFile';
import { isNotNil } from 'helpers/isNotNil';

type Props = StrongOmit<FileButtonProps, 'onChange' | 'multiple' | 'children'> & {
  label?: string;
  description?: string;
  value?: File;
  onChange: (img: File) => void;
  /** Max file size in bytes. Default is 2MB. */
  maxSize?: number;
  /** existing `fileId` */
  existingFile?: FileId;
  disabled?: boolean;
  required?: boolean;
};

const accept = ['image/png', 'image/jpg', 'image/jpeg'];

export const ImageUpload = ({
  maxSize = 2 * 1000 * 1024,
  value,
  onChange,
  label,
  description,
  disabled,
  required,
  existingFile,
  ...props
}: Props) => {
  const [__value, __onChange] = useUncontrolled({
    value,
    onChange,
  });
  const [previewUrl, setPreviewUrl] = useState<string>();
  useEffect(() => {
    if (isNotNil(__value)) {
      const objectUrl = URL.createObjectURL(__value);
      setPreviewUrl(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
    }
    return undefined;
  }, [__value]);

  const [existingImageUrl] = usePresignedFile(existingFile);

  const imageSrc = previewUrl ?? existingImageUrl;

  return (
    <Stack gap={0}>
      {!!label && <InputLabel required={required}>{label}</InputLabel>}
      <Flex gap="md">
        <FileButton
          accept={accept.join(',')}
          onChange={(img) => {
            if (!img) return;
            if (!accept.some((a) => a.toLocaleLowerCase() === img.type.toLocaleLowerCase())) {
              notifications.show({
                title: 'Unsupported file type',
                message: img.name,
                color: 'red',
              });
              return;
            }
            if (img.size > maxSize) {
              notifications.show({
                title: 'File too large',
                message: 'Max file size is 2MB.',
                color: 'red',
              });
              return;
            }
            __onChange(img);
          }}
          {...props}
        >
          {(uploadProps) => (
            <AgGridStyleTooltip
              position="left"
              maw="100vw"
              mah="100vh"
              disabled={!imageSrc}
              label={
                <Avatar
                  radius="sm"
                  src={imageSrc}
                  w="auto"
                  h="auto"
                  styles={{
                    image: {
                      objectFit: 'contain',
                      width: 'auto',
                      height: 'auto',
                      maxHeight: 'calc(100vh - 2rem)',
                      maxWidth: 'calc(100vw - 2rem)',
                    },
                  }}
                />
              }
            >
              <ActionIcon color="gray" disabled={disabled} {...uploadProps} size={88} variant="subtle">
                <Avatar
                  radius="sm"
                  src={imageSrc}
                  size="xl"
                  styles={{
                    image: { objectFit: 'contain' },
                  }}
                >
                  <EvolveIcon icon="Upload" size="xl" />
                </Avatar>
                <Overlay className="upload-overlay" bg="gray.2">
                  <Flex justify="center" align="center" h="100%">
                    <EvolveIcon icon="Upload" size="xl" />
                  </Flex>
                </Overlay>
                <LoadingOverlay visible={!!previewUrl && disabled} />
              </ActionIcon>
            </AgGridStyleTooltip>
          )}
        </FileButton>
        <Text c="dimmed" fz="sm">
          {description || (
            <>
              Select an image you want to upload. Images should not be more than 2MB in size. Only GIF, PNG, or JPEG
              files are accepted. {required ? 'Required.' : 'Optional.'}
            </>
          )}
        </Text>
      </Flex>
    </Stack>
  );
};
